import { useRef, useState } from 'react';
import {
  Button,
  Modal,
  SelectField,
  TextField,
} from '@leadsales/design-system/react';
import { ChatBubbleLeftRightIcon, LinkIcon } from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';
import { v4 as uuid } from 'uuid';
import useChatActions from 'src/components/Chat/Chat.actions';
import { useChatContext } from 'src/components/Chat/Chat.hook';
import { parseTikTokPostId, tTikTokRichContent } from '../../TikTok.types';
import './Capabilities.styles.scss';

type Kind = 'template' | 'share_post';
export function TikTokRichComposer({
  kind,
  onClose,
  onSend,
}: {
  kind: Kind;
  onClose: () => void;
  onSend: (content: tTikTokRichContent, id: string) => Promise<boolean>;
}) {
  const { t } = useTranslation('tiktok/tiktok');
  const [post, setPost] = useState('');
  const [title, setTitle] = useState('');
  const [type, setType] = useState<'QA_BUTTON_CARD' | 'QA_LINK_CARD'>(
    'QA_BUTTON_CARD',
  );
  const [answers, setAnswers] = useState(['']);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const sending = useRef(false);
  const id = useRef(uuid());
  const limit = type === 'QA_BUTTON_CARD' ? 20 : 40;
  async function submit() {
    if (sending.current) return;
    let content: tTikTokRichContent;
    if (kind === 'share_post') {
      const itemId = parseTikTokPostId(post);
      if (!itemId) {
        setError('postError');
        return;
      }
      content = { kind: 'share_post', share_post: { item_id: itemId } };
    } else {
      if (
        !title.trim() ||
        Array.from(title).length > 40 ||
        answers.length < 1 ||
        answers.length > 3 ||
        answers.some((a) => !a.trim() || Array.from(a).length > limit)
      ) {
        setError('cardError');
        return;
      }
      content = {
        kind: 'template',
        template: {
          type,
          title,
          buttons: answers.map((answer, index) => ({
            type: 'REPLY',
            title: answer,
            id: `reply-${index + 1}`,
          })),
        },
      };
    }
    sending.current = true;
    setBusy(true);
    setError('');
    try {
      if (await onSend(content, id.current)) onClose();
      else setError('sendError');
    } catch {
      setError('sendError');
    } finally {
      sending.current = false;
      setBusy(false);
    }
  }
  return (
    <Modal
      open
      onClose={() => {
        if (!busy) onClose();
      }}
      size="md"
      closeOnBackdrop={!busy}
    >
      <Modal.Header
        title={t(
          kind === 'template' ? 'capabilities.card' : 'capabilities.post',
        )}
        closeLabel={t('capabilities.cancel')}
      />
      <Modal.Body>
        <form
          id="tiktok-rich-form"
          className="TikTokCapabilities__Fields"
          onSubmit={(event) => {
            event.preventDefault();
            void submit();
          }}
        >
          {error && <p role="alert">{t(`capabilities.${error}`)}</p>}
          {kind === 'share_post' ? (
            <TextField
              required
              label={t('capabilities.postLabel')}
              helperText={t('capabilities.postHelp')}
              value={post}
              onChange={(e) => setPost(e.target.value)}
              disabled={busy}
            />
          ) : (
            <>
              <SelectField
                label={t('capabilities.cardType')}
                value={type}
                onChange={(e) => setType(e.target.value as typeof type)}
                disabled={busy}
              >
                <option value="QA_BUTTON_CARD">
                  {t('capabilities.buttons')}
                </option>
                <option value="QA_LINK_CARD">{t('capabilities.links')}</option>
              </SelectField>
              <TextField
                required
                label={t('capabilities.question')}
                maxLength={40}
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                disabled={busy}
              />
              {answers.map((answer, index) => (
                <div key={index} className="TikTokCapabilities__Fields">
                  <TextField
                    required
                    label={t('capabilities.answer', { number: index + 1 })}
                    maxLength={limit}
                    value={answer}
                    onChange={(e) =>
                      setAnswers((values) =>
                        values.map((value, i) =>
                          i === index ? e.target.value : value,
                        ),
                      )
                    }
                    disabled={busy}
                  />
                  {answers.length > 1 && (
                    <Button
                      color="ghost"
                      type="button"
                      onClick={() =>
                        setAnswers((values) =>
                          values.filter((_, i) => i !== index),
                        )
                      }
                      disabled={busy}
                    >
                      {t('capabilities.removeAnswer', { number: index + 1 })}
                    </Button>
                  )}
                </div>
              ))}
              {answers.length < 3 && (
                <Button
                  color="ghost"
                  type="button"
                  onClick={() => setAnswers((values) => [...values, ''])}
                  disabled={busy}
                >
                  {t('capabilities.addAnswer')}
                </Button>
              )}
            </>
          )}
        </form>
      </Modal.Body>
      <Modal.Footer>
        <div className="TikTokCapabilities__Actions">
          <Button color="ghost" onClick={onClose} disabled={busy}>
            {t('capabilities.cancel')}
          </Button>
          <Button form="tiktok-rich-form" type="submit" loading={busy}>
            {t('capabilities.send')}
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
}
export default function TikTokRichActions() {
  const { t } = useTranslation('tiktok/tiktok');
  const { blockTextArea, selectedChat } = useChatContext();
  const { sendTikTokRichMessage } = useChatActions();
  const [kind, setKind] = useState<Kind>();
  return (
    <>
      <Button
        iconOnly
        color="ghost"
        aria-label={t('capabilities.card')}
        disabled={blockTextArea}
        onClick={() => setKind('template')}
      >
        <ChatBubbleLeftRightIcon />
      </Button>
      <Button
        iconOnly
        color="ghost"
        aria-label={t('capabilities.post')}
        disabled={blockTextArea}
        onClick={() => setKind('share_post')}
      >
        <LinkIcon />
      </Button>
      {kind && (
        <TikTokRichComposer
          key={`${selectedChat?.id}:${kind}`}
          kind={kind}
          onClose={() => setKind(undefined)}
          onSend={sendTikTokRichMessage}
        />
      )}
    </>
  );
}
